import { Container, randomFloat } from '../../odie';
import { IAnimation } from '../../lib/pattern/IAnimation';
import { MovieClip, getRelativeOffset } from '@play-co/timestep-core/ui';
import { getGoals } from 'src/game/scenes/Match2Scene';
import { getMatch2AssetPath } from '../util/assets';
import gsap from 'gsap';
import { StarGlareAnimation } from './StarGlareAnimation';
import { config } from '../defs/config';

const PI_2 = Math.PI * 2;

export type BeeCollectedAnimationOptions = {
  origin: Container;
};

export class BeeCollectedAnimation extends Container implements IAnimation {
  constructor(private options: BeeCollectedAnimationOptions) {
    super();
  }

  public static assets(): string[] {
    return [];
  }

  public async start(): Promise<void> {
    await this._flyToGoalBar();
  }

  public stop() {}

  private async _flyToGoalBar() {
    const goals = getGoals()?.getGoal('nest');

    // Skip if the goal is undefined - can happen if game is closed during animation.
    if (!goals) return;

    const bee = this._createBee();
    const glare = new StarGlareAnimation();

    goals.addSubview(bee);

    // Set initial position.
    const { x, y } = getRelativeOffset(this.view, goals);
    bee.updateOpts({
      x,
      y,
      scale: 0.5,
    });

    const startX = bee.style.x;
    const distX = goals.style.width * 0.5 - bee.style.x;
    const distY = bee.style.y - goals.style.height * 0.5;
    const rangeX = randomFloat(80, 120);
    const dirX = Math.random() > 0.5 ? 1 : -1;
    const dist = Math.sqrt(distX * distX + distY * distY);
    const duration = (dist / config.tile.size) * 0.075;
    let prevX = bee.style.x;
    let prevY = bee.style.y;

    await gsap
      .timeline({
        onUpdate: () => {
          const diffY = bee.style.y - goals.style.height * 0.5;
          const ratioY = 1 - diffY / distY;
          const baseX = ratioY * distX;
          const offsetX = Math.sin(ratioY * Math.PI * 2) * rangeX;

          bee.style.x = startX + baseX + offsetX * dirX;

          const dx = bee.style.x - prevX;
          const dy = bee.style.y - prevY;
          const angle = ((Math.atan2(dy, dx) % PI_2) + PI_2) % PI_2;

          bee.style.r = Math.PI * 0.5 + angle;
          prevX = bee.style.x;
          prevY = bee.style.y;
        },
      })
      .to(
        bee.style,
        {
          duration,
          y: goals.style.height * 0.5,
          ease: 'quad.inOut',
        },
        0,
      )
      .to(
        bee.style,
        {
          duration: duration * 0.5,
          scale: 1.4,
          ease: 'quad.inOut',
        },
        0,
      )
      .to(
        bee.style,
        {
          duration: duration * 0.5,
          scale: 0.7,
          ease: 'quad.inOut',
        },
        duration * 0.5,
      );

    bee.removeFromSuperview();

    glare.x = goals.style.width * 0.5;
    glare.y = goals.style.height * 0.5;
    goals.addSubview(glare.view);
    await glare.start();
    glare.view.removeFromSuperview();
  }

  private _createBee(): MovieClip {
    const movieclip = new MovieClip({
      url: getMatch2AssetPath('blocks/nest'),
      scale: 0.9,
      fps: 20,
    });
    movieclip.play('bee_idle', null, true);
    return movieclip;
  }
}
